import { SquidDataType } from "@0xsquid/squid-types";
import { erc20Abi } from "viem";
import { useReadContract } from "wagmi";
import { nativeEvmTokenAddress } from "../../core/constants";
import { parseEvmAddress } from "../../services";

interface Erc20AllowanceParams {
  tokenAddress: string | undefined;
  ownerAddress: string | undefined;
  spenderAddress: string | undefined;
  amount: bigint | undefined;
  chainId: number | undefined;
  transactionType: SquidDataType | undefined;
  enabled?: boolean;
}

export const useErc20Allowance = ({
  tokenAddress,
  ownerAddress,
  spenderAddress,
  amount = BigInt(0),
  chainId,
  transactionType,
  enabled = true,
}: Erc20AllowanceParams) => {
  const parsedTokenAddress = parseEvmAddress(tokenAddress);
  const parsedOwnerAddress = parseEvmAddress(ownerAddress);
  const parsedSpenderAddress = parseEvmAddress(spenderAddress);
  const isNativeToken =
    parsedTokenAddress?.toLowerCase() === nativeEvmTokenAddress.toLowerCase();
  const isDepositAddressTx =
    !!transactionType &&
    [
      SquidDataType.DepositAddressCalldata,
      SquidDataType.DepositAddressWithSignature,
      SquidDataType.DepositAddressWithMemo,
    ].includes(transactionType);

  const result = useReadContract({
    abi: erc20Abi,
    address: parsedTokenAddress ?? undefined,
    functionName: "allowance",
    chainId,
    args:
      parsedOwnerAddress && parsedSpenderAddress
        ? [parsedOwnerAddress, parsedSpenderAddress]
        : undefined,
    query: {
      enabled:
        enabled &&
        Boolean(
          parsedTokenAddress &&
            parsedOwnerAddress &&
            parsedSpenderAddress &&
            !isNativeToken &&
            !isDepositAddressTx
        ),
    },
  });

  if (!parsedTokenAddress || !parsedOwnerAddress || !parsedSpenderAddress) {
    return { hasAllowance: false };
  }

  // Native tokens and deposit address transactions don't need approval
  if (isNativeToken || isDepositAddressTx) {
    return { hasAllowance: true };
  }

  const allowanceInWei = result.data ?? BigInt(0);
  const hasAllowance = allowanceInWei >= amount;

  return {
    query: result,
    hasAllowance,
    allowanceInWei,
  };
};
